import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha } from '@mui/material/styles';
import algolia from '../../../utilities/algolia';
import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import constants from '../../../utilities/constants';
import CustomerSelectList from '../../../components/CustomerSelectList';
import NewCustomerModal from '../../../components/NewCustomerModal';

export default function ConsignorCard({ customer = {}, stockNumber }) {
  const safe = customer && typeof customer === 'object' ? customer : {};

  const [inputValue, setInputValue] = React.useState(safe.display_name || '');
  const [committedName, setCommittedName] = React.useState(safe.display_name || '');
  const [results, setResults] = React.useState([]);
  const [selected, setSelected] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [newCustomerOpen, setNewCustomerOpen] = React.useState(false);
  const [pendingName, setPendingName] = React.useState('');
  const inputWrapperRef = React.useRef(null);

  React.useEffect(() => {
    setInputValue(safe.display_name || '');
    setCommittedName(safe.display_name || '');
  }, [safe.display_name, safe.id]);

  const search = async (query) => {
    if (!query) { setResults([]); return; }
    const index = algolia.client.initIndex('customers');
    const { hits } = await index.search(query);
    const mapped = hits.map(hit => ({ ...hit, action: makeSelectAction(hit), label: getLabel(hit) }));
    setResults([{ label: 'Make New Customer', action: () => openNewCustomer(query) }, ...mapped]);
  };

  const makeSelectAction = (hit) => async () => {
    try {
      setLoading(true);
      const name = `${hit.first_name || ''}${hit.last_name ? ' ' : ''}${hit.last_name || ''}`;
      await setConsignor(stockNumber, hit.objectID);
      setInputValue(name);
      setCommittedName(name);
      setResults([]);
    } catch (e) {
      console.error(e);
      setInputValue(committedName);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const openNewCustomer = (rawName) => {
    setPendingName(rawName);
    setResults([]);
    setNewCustomerOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') results[selected]?.action?.();
    if (e.key === 'Escape') { setInputValue(committedName); setResults([]); }
    if (e.key === 'ArrowUp') setSelected(s => Math.max(0, s - 1));
    if (e.key === 'ArrowDown') setSelected(s => Math.min(results.length - 1, s + 1));
  };

  const handleBlur = () => {
    setTimeout(() => { setInputValue(committedName); setResults([]); }, 200);
  };

  const handleClear = async () => {
    if (!window.confirm('Remove this consignor from the car?')) return;
    setInputValue('');
    setCommittedName('');
    setResults([]);
    await removeConsignor(stockNumber);
  };

  const hasConsignor = !!safe.display_name;
  const initials = getInitials(safe.display_name || '');

  if (hasConsignor) {
    return (
      <Box sx={{ px: 2, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            bgcolor: '#023e84',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 15, letterSpacing: 0.5 }}>
              {initials}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {safe.display_name}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
              {safe.email && (
                <Chip
                  icon={<EmailIcon sx={{ fontSize: '13px !important' }} />}
                  label={safe.email}
                  size="small"
                  component="a"
                  href={`mailto:${safe.email}`}
                  clickable
                  sx={{ fontSize: 11, height: 22 }}
                />
              )}
              {safe.phone_number && (
                <Chip
                  icon={<PhoneIcon sx={{ fontSize: '13px !important' }} />}
                  label={safe.phone_number}
                  size="small"
                  component="a"
                  href={`tel:${safe.phone_number}`}
                  clickable
                  sx={{ fontSize: 11, height: 22 }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
            <IconButton size="small" title="View profile" data-sold-ok onClick={(e) => {
              // Edit the doc the car points at — see the note in SalesComponents/CustomerCard.js.
              const cid = safe.id;
              if (!cid) return;
              if (e.ctrlKey || e.metaKey) {
                window.open(`${window.location.pathname}?customer=${cid}`, '_blank');
                return;
              }
              StateManager.openCustomerModal(cid);
            }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" title="Remove consignor" onClick={handleClear}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pb: 1.5 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '50%',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <PersonOutlineIcon sx={{ fontSize: 24, color: 'primary.main', opacity: 0.7 }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Who owns this car?</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Search existing or create new customer
          </Typography>
        </Box>
      </Box>

      <Box ref={inputWrapperRef}>
        <TextField
          fullWidth
          size="small"
          label="Search consignor"
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); search(e.target.value); }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={loading}
          autoComplete="off"
          InputProps={{ endAdornment: loading ? <CircularProgress size={16} /> : null }}
        />
        <CustomerSelectList
          results={results}
          selected={selected}
          onSelect={(item) => item.action?.()}
          anchorEl={inputWrapperRef.current}
        />
      </Box>

      <NewCustomerModal
        open={newCustomerOpen}
        onClose={() => setNewCustomerOpen(false)}
        stockNumber={stockNumber}
        type="consignor"
        initialName={pendingName}
        onSaved={(cust) => {
          setInputValue(cust.display_name);
          setCommittedName(cust.display_name);
          StateManager.updateCar({ consignor: cust });
        }}
      />
    </Box>
  );
}

const getInitials = (name) => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getLabel = (hit) => {
  if (hit._highlightResult) {
    const h = hit._highlightResult;
    const fn = (h.first_name?.value || hit.first_name || '').replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
    const ln = (h.last_name?.value || hit.last_name || '').replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
    return `${fn} ${ln}`.trim();
  }
  return `${hit.first_name || ''} ${hit.last_name || ''}`.trim();
};

const removeConsignor = async (stockNumber) => {
  const removal = { consignor: firebase.firestore.FieldValue.delete() };
  await Promise.all([
    firebase.firestore().doc('cars/' + stockNumber).set(removal, { merge: true }),
    firebase.firestore().doc('deals/' + stockNumber).set(removal, { merge: true }),
  ]);
  StateManager.updateCar({ consignor: null });
};

const setConsignor = async (stockNumber, id) => {
  if (!constants.isValidCustomerId(id)) throw new Error('Customer not found');
  const db = firebase.firestore();
  await Promise.all([
    db.doc('cars/' + stockNumber).set({ consignor: id }, { merge: true }),
    db.doc('deals/' + stockNumber).set({ consignor: id }, { merge: true }),
  ]);
  const snap = await db.doc(`customers/${id}`).get();
  if (!snap.exists) throw new Error('Customer not found');
  const data = snap.data();
  const customer = {
    ...data,
    id,
    display_name: `${data.first_name || ''}${data.last_name ? ' ' : ''}${data.last_name || ''}`.trim(),
  };
  StateManager.updateCar({ consignor: customer });
  return customer;
};
